import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import { dispatch } from "../../state/store";
import { setHardwareRefresh, setMercuryHardwareRefresh } from "../../state/ducks";

import { alignItems, hardwareSections, strCode } from "../../consts";
import { t } from "../../services";
import { withSection } from "../../hoc";
import { hardwareUtils, locationUtil } from "../../utils";
import {
  useDeleteStore,
  useFetchHardware,
  useFetchMercuryHardware,
  useSaveStore,
  useUpdateHardwareLocation,
  useUpdateLocationHardware,
  useUpdateMercuryHardware
} from "../../apiHooks";

import {
  FlexContainers,
  Heading5,
  PageContainer,
  RefreshButton,
  WrapperContainer
} from "../../components";
import {
  HardwareControlOptions,
  HardwareLocationModal,
  HardwareTable,
  LocationHardwareModal,
  LocationHardwareTable,
  NewMercuryHardwareModal,
  Stores
} from "../../containers";

const hardwarePages = [
  { id: hardwareSections.PETIOLE, title: strCode.PETIOLE_TITLE },
  { id: hardwareSections.MERCURY, title: strCode.MERCURY_TITLE }
];

const viewOptionIds = {
  hardware: "hardware",
  locations: "locations"
};

const viewOptions = [
  { id: viewOptionIds.hardware, value: strCode.VIEW_BY_HARDWARE },
  { id: viewOptionIds.locations, value: strCode.VIEW_BY_LOCATIONS }
];

const HardwarePage = () => {
  const [selectedHardware, setSelectedHardware] = useState(null);
  const [selectedLocationHardware, setSelectedLocationHardware] = useState(null);

  const [activePage, setActivePage] = useState(hardwareSections.PETIOLE);
  const [selectedView, setSelectedView] = useState(viewOptions[0]);
  const [searchedText, setSearchedText] = useState("");

  const [locationsModalActive, setLocationsModalActive] = useState(false);

  const { mutationCallback: onSaveStore } = useSaveStore();
  const { mutationCallback: onDeleteStore } = useDeleteStore();

  const {
    fetchCallback: fetchHardware,
    data: hardware,
    loading: hardwareLoading,
    refresh: hardwareRefresh,
    error: hardwareError
  } = useFetchHardware();

  const {
    fetchCallback: fetchMercuryHardware,
    data: mercuryHardware,
    loading: mercuryHardwareLoading,
    refresh: mercuryHardwareRefresh
  } = useFetchMercuryHardware();

  const { mutationCallback: updateHardwareLocation, loading: updateHardwareLocationLoading } =
    useUpdateHardwareLocation();

  const { mutationCallback: updateLocationHardware, loading: updateLocationHardwareLoading } =
    useUpdateLocationHardware();

  const { mutationCallback: updateMercuryHardware, loading: updateMercuryHardwareLoading } =
    useUpdateMercuryHardware();

  const locations = useSelector((state) => state.configs.locations);
  const locationsList = useMemo(() => locationUtil.formatLocations(locations), [locations]);

  const locationHardwareData = useMemo(() => {
    return hardwareUtils.getLocationHardwareData(hardware);
  }, [hardware]);

  const hardwareIds = useMemo(() => {
    return hardwareUtils.getHardwareIds(hardware);
  }, [hardware]);

  const isMercury = useMemo(() => {
    return activePage === hardwareSections.MERCURY;
  }, [activePage]);

  const mercuryLocationHardwareData = useMemo(() => {
    return hardwareUtils.getLocationHardwareData(mercuryHardware);
  }, [mercuryHardware, isMercury]);

  const mercuryHardwareIds = useMemo(() => {
    return hardwareUtils.getHardwareIds(mercuryHardware);
  }, [mercuryHardware, isMercury]);

  useEffect(() => {
    if (activePage === hardwareSections.PETIOLE) {
      fetchHardware();
    } else {
      fetchMercuryHardware();
    }
  }, [activePage]);

  useEffect(() => {
    if (hardwareRefresh) {
      fetchHardware(true);
    }
  }, [hardwareRefresh]);

  useEffect(() => {
    if (mercuryHardwareRefresh) {
      fetchMercuryHardware(true);
    }
  }, [mercuryHardwareRefresh]);

  const onSaveHardwareLocation = async (selectedLocation) => {
    const locationId = selectedLocation?.id;
    const market = locationsList?.find((location) => location.id === locationId)?.market ?? null;

    const hubLocation = { ...selectedHardware, locationId: locationId ?? null, market };

    await updateHardwareLocation(hubLocation);
    setSelectedHardware(null);
  };

  const onSaveLocationHardware = async (selectedHardwares) => {
    const locationHubs = {
      ...selectedLocationHardware,
      hubIds: selectedHardwares.map((el) => el.id)
    };

    if (isMercury) {
      const data = {
        routerIds: locationHubs?.hubIds,
        locationId: locationHubs?.locationId,
        market: locationHubs?.market,
        locationName: locationHubs?.locationName
      };

      await updateLocationHardware(data, true);
    } else {
      await updateLocationHardware(locationHubs);
    }

    setSelectedLocationHardware(null);
  };

  const onSaveMercuryHardware = async (mercuryHardware) => {
    const isNew = !selectedHardware?.routerId;

    await updateMercuryHardware(mercuryHardware, isNew);

    setSelectedHardware(null);
  };

  return (
    <PageContainer alignItems={alignItems.start}>
      <WrapperContainer
        Container={FlexContainers.FlexColumnStart}
        mb={16}
        alignItems="start"
        width="100%">
        <WrapperContainer Container={FlexContainers.FlexRowStart} width="100%" gap={16}>
          <Heading5>{t(strCode.HARDWARES_TITLE)}</Heading5>

          <RefreshButton
            isLoading={hardwareLoading || mercuryHardwareLoading}
            handleRefresh={() => {
              dispatch(setHardwareRefresh(true));
              dispatch(setMercuryHardwareRefresh(true));
            }}
          />
        </WrapperContainer>
      </WrapperContainer>

      <HardwareControlOptions
        hardwarePages={hardwarePages}
        activePage={activePage}
        searchedText={searchedText}
        viewOptions={viewOptions}
        selectedView={selectedView}
        loading={
          hardwareLoading ||
          mercuryHardwareLoading ||
          updateHardwareLocationLoading ||
          updateLocationHardwareLoading ||
          updateMercuryHardwareLoading
        }
        setActivePage={setActivePage}
        setSearchedText={setSearchedText}
        setSelectedView={setSelectedView}
        setLocationsModalActive={setLocationsModalActive}
        onCreateNewHardware={() => setSelectedHardware({})}
      />

      <WrapperContainer
        Container={FlexContainers.FlexColumnStart}
        alignItems={alignItems.start}
        width="100%">
        {selectedView?.id === viewOptionIds.hardware ? (
          <HardwareTable
            hardwareData={(isMercury ? mercuryHardware : hardware) ?? []}
            locations={locationsList}
            isMercury={isMercury}
            searchedText={searchedText}
            loading={hardwareLoading || mercuryHardwareLoading}
            error={!!hardwareError}
            onEdit={setSelectedHardware}
          />
        ) : (
          <LocationHardwareTable
            locationHardwareData={
              (isMercury ? mercuryLocationHardwareData : locationHardwareData) ?? []
            }
            searchedText={searchedText}
            isMercury={isMercury}
            loading={hardwareLoading || mercuryHardwareLoading}
            error={!!hardwareError}
            onEdit={setSelectedLocationHardware}
          />
        )}
      </WrapperContainer>

      {isMercury ? (
        <NewMercuryHardwareModal
          hardwareData={selectedHardware}
          locations={locationsList}
          loading={updateMercuryHardwareLoading}
          active={!!selectedHardware}
          setActive={() => setSelectedHardware(null)}
          onSave={onSaveMercuryHardware}
        />
      ) : (
        <HardwareLocationModal
          locations={locationsList}
          hardware={selectedHardware}
          loading={updateHardwareLocationLoading}
          active={!!selectedHardware}
          setActive={() => setSelectedHardware(null)}
          onSave={onSaveHardwareLocation}
        />
      )}

      <LocationHardwareModal
        hardwares={isMercury ? mercuryHardwareIds : hardwareIds}
        locationHardware={selectedLocationHardware}
        locations={locationsList}
        isMercury={isMercury}
        loading={updateLocationHardwareLoading}
        active={!!selectedLocationHardware}
        setActive={() => setSelectedLocationHardware(null)}
        onSave={onSaveLocationHardware}
      />

      <Stores
        active={locationsModalActive}
        setActive={setLocationsModalActive}
        onSaveStore={onSaveStore}
        onDeleteStore={onDeleteStore}
      />
    </PageContainer>
  );
};

export default withSection(HardwarePage);
